import { create } from 'zustand';

interface ScheduleSelectionState {
  /** Currently active (viewed) schedule ID */
  selectedScheduleId: string | null;
  /** Checked schedule IDs for bulk operations */
  checkedIds: Set<string>;
  /** Checked folder/group IDs for bulk operations */
  checkedGroupIds: Set<string>;
  /** Whether bulk-selection checkboxes are visible */
  showCheckboxes: boolean;
  /**
   * Schedules PROVEN not to exist — the detail panel asked the server for one and it was gone
   * (deleted or trashed, by this user or another). Monotonic: ids are only ever added, for the
   * lifetime of the page.
   *
   * It exists to break a render loop (#2416 / #2336). The sidebar list can serve a deleted schedule
   * for a while — it is a cached list, and a remote delete reaches it by refetch — so auto-select
   * would pick that id, the detail panel would find it missing and clear the selection, and
   * auto-select would pick it straight back. Each flip remounts FullCalendar, whose custom-rendering
   * store setStates per remount, so React hit "Maximum update depth exceeded" and the whole page
   * fell through to the route error boundary.
   *
   * A SET, not one id, and monotonic — this is the part that is easy to simplify away and must not
   * be. Remembering only the last missing schedule is enough for one deletion, which is what both
   * tickets were reported from, but not for two at once: auto-select picks A, the panel records A,
   * it picks B, the panel records B and FORGETS A, it picks A again — the same loop, restored.
   * Trashing a folder of schedules deletes several at once, so that case is routine, not exotic.
   * Anything that merely *reacted* to a deletion could likewise be undone by the next stale render.
   */
  missingScheduleIds: Set<string>;
  selectSchedule: (id: string | null) => void;
  /** Clear the selection because `id` no longer exists, and remember it as missing. */
  clearMissingSchedule: (id: string) => void;
  toggleChecked: (id: string) => void;
  toggleCheckedGroup: (id: string) => void;
  checkAll: (scheduleIds: string[], groupIds?: string[]) => void;
  /** Uncheck specific ids (schedules or folders) — e.g. rows that left the Favorites filter. */
  uncheck: (ids: string[]) => void;
  clearChecked: () => void;
  isChecked: (id: string) => boolean;
  isGroupChecked: (id: string) => boolean;
  setShowCheckboxes: (show: boolean) => void;
}

export const useScheduleSelectionStore = create<ScheduleSelectionState>((set, get) => ({
  selectedScheduleId: null,
  checkedIds: new Set(),
  checkedGroupIds: new Set(),
  showCheckboxes: false,
  missingScheduleIds: new Set(),
  selectSchedule: (selectedScheduleId) => set({ selectedScheduleId }),
  clearMissingSchedule: (id) =>
    set((state) => {
      // Both halves in one action, as #2416 had it: clear the selection AND record the fact.
      if (!id) return { selectedScheduleId: null };
      if (state.missingScheduleIds.has(id)) return { selectedScheduleId: null };
      const missingScheduleIds = new Set(state.missingScheduleIds);
      missingScheduleIds.add(id);
      return { selectedScheduleId: null, missingScheduleIds };
    }),
  toggleChecked: (id) =>
    set((s) => {
      const next = new Set(s.checkedIds);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return { checkedIds: next };
    }),
  toggleCheckedGroup: (id) =>
    set((s) => {
      const next = new Set(s.checkedGroupIds);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return { checkedGroupIds: next };
    }),
  checkAll: (scheduleIds, groupIds = []) =>
    set({ checkedIds: new Set(scheduleIds), checkedGroupIds: new Set(groupIds) }),
  uncheck: (ids) =>
    set((s) => {
      if (ids.length === 0) return s;
      const nextIds = new Set(s.checkedIds);
      const nextGroupIds = new Set(s.checkedGroupIds);
      let changed = false;
      for (const id of ids) {
        if (nextIds.delete(id)) changed = true;
        if (nextGroupIds.delete(id)) changed = true;
      }
      return changed ? { checkedIds: nextIds, checkedGroupIds: nextGroupIds } : s;
    }),
  clearChecked: () =>
    set({ checkedIds: new Set(), checkedGroupIds: new Set(), showCheckboxes: false }),
  isChecked: (id) => get().checkedIds.has(id),
  isGroupChecked: (id) => get().checkedGroupIds.has(id),
  setShowCheckboxes: (showCheckboxes) => set({ showCheckboxes }),
}));
